import { useCallback } from 'react';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { fg } from '@confluence/feature-gating';

import { type ContentMode } from './presenceTypes';
import { type PresenceParticipant, useActiveTelepointerBySessions } from './presence-store';

type UseScrollToTelepointerArgs = {
	contentType: string;
	contentMode: ContentMode;
	participant: PresenceParticipant;
};

export const useScrollToTelepointer = ({
	contentType,
	contentMode,
	participant,
}: UseScrollToTelepointerArgs) => {
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();
	const [activeTelepointer] = useActiveTelepointerBySessions(participant?.sessions);

	const isEligibleToScroll =
		// This is to filter out cases like where we have a user but no telepointer data; e.g. just joined in
		activeTelepointer !== undefined &&
		// Current user won't have the onClick handler for scroll
		!participant?.isCurrentUser &&
		// This feature is only eligible for page and blogposts
		(contentType === 'page' || contentType === 'blogpost') &&
		// ...and not in view mode
		contentMode !== 'view' &&
		// Only available if the FG allows us
		fg('confluence_team_presence_scroll_to_pointer');

	const scrollToTelepointer = useCallback(() => {
		if (!isEligibleToScroll) {
			return;
		}

		fullPageEditorPresetAPI?.core?.actions?.scrollToPos(Number(activeTelepointer), {
			behavior: 'smooth',
			block: 'center',
		});
	}, [isEligibleToScroll, activeTelepointer, fullPageEditorPresetAPI]);

	return { isEligibleToScroll, scrollToTelepointer };
};
